import { Controller, useFormContext } from 'react-hook-form';
import {
  Clone,
  DatePicker,
  Delete,
  Select,
  TimeWindowControl,
  RefreshTW,
  Warning,
} from 'components';
import { Api } from 'api';
import { TreeSelect } from 'components/TreeSelect';
import { useEffect, useState } from 'react';
import { CircleButton } from 'components/CircleButton';
import { useCompositeWeightedTimeWindowData } from '../hooks';
import { CompositeWeightedPreview } from 'types/CompositeWeighted';
import { useFormErrors } from 'hooks';

type Props = {
  timeWindow: Record<string, any>;
  index: number;
  isOpened: boolean;
  onClone: Function;
  onRemove: Function;
  onRefresh: Function;
  refreshDates: Function;
  handleToggleHeaders: Function;
};

export const CompositeWeightedTimeWindowHeader = ({
  isOpened,
  onClone,
  onRemove,
  timeWindow,
  handleToggleHeaders,
  refreshDates,
  index,
}: Props) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useFormContext();
  const [propertyValueOptions, setPropertyValueOptions] = useState([]);
  const { data: assetOptions, isLoading: loadingAssets } =
    Api.lookup.GetAssetSelectionSets();
  const { data: tagRefOptions, isLoading: loadingProperties } =
    Api.lookup.GetTagRefPropertyDefinitions();
  const fieldName = `timeWindows[${index}]`;
  const lineItemsFieldName = `${fieldName}.lineItems`;
  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName,
    index,
  });
  const { refreshCompositeWeightedTimeWindowData, data, isLoading } =
    useCompositeWeightedTimeWindowData({ fieldName });
  const timeWindows = getValues('timeWindows');

  const refreshLineItems = (
    refreshedComponents: Array<CompositeWeightedPreview>
  ) => {
    const lineItems = getValues(lineItemsFieldName);
    const refreshedLineItems = refreshedComponents.map(
      (c: CompositeWeightedPreview) => {
        const lineItem = lineItems.find(
          (l: any) => c.weightingComponentTag.id === l.weightingComponentTagId
        );
        if (!lineItem) {
          return {
            returnComponentResourceId: c.returnComponent?.resourceId ?? null,
            returnComponentName: c.returnComponent?.name ?? '',
            weightingComponentTagId: c.weightingComponentTag.id,
            weightingComponentName: c.weightingComponentTag.name,
          };
        } else {
          return {
            ...lineItem,
            weightingComponentName: c.weightingComponentTag.name,
            returnComponentResourceId:
              lineItem.returnComponentResourceId ??
              c.returnComponent?.resourceId ??
              null,
            returnComponentName:
              lineItem.returnComponentName ?? c.returnComponent?.name ?? '',
          };
        }
      }
    );

    setValue(lineItemsFieldName, refreshedLineItems, { shouldDirty: true });
  };

  useEffect(() => {
    if (data && data.data.length) {
      refreshLineItems(data.data);
    }
  }, [data]);

  const watchProperty = watch(`${fieldName}.tagRefPropertyDefinitionId`);

  useEffect(() => {
    const option = tagRefOptions?.find((tag) => tag.value === watchProperty);
    if (option) {
      setPropertyValueOptions(option.tags);
    }
  }, [watchProperty, tagRefOptions]);

  useEffect(() => {
    setValue(`${fieldName}.loadingWeightingComponents`, isLoading, {
      shouldDirty: false,
    });
  }, [isLoading]);

  return (
    <div
      key={index}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <div
        style={{
          maxWidth: '90%',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '10px',
          paddingBottom: '6px',
        }}
      >
        <TimeWindowControl label='From Date'>
          <Controller
            name={`${fieldName}.fromDate`}
            control={control}
            render={({ field: { onChange, value } }) => {
              const dateValue =
                typeof value === 'string' ? new Date(value) : value;

              return (
                <DatePicker
                  key='fromDate'
                  testid={`${fieldName}.fromDate`}
                  onChange={onChange}
                  onCalendarClose={() => refreshDates()}
                  selected={dateValue}
                  errorMessage={getErrorMessage(`${fieldName}.fromDate`)}
                  style={{ width: 120 }}
                  size='sm'
                />
              );
            }}
          />
        </TimeWindowControl>
        <TimeWindowControl label='To Date'>
          <Controller
            control={control}
            name={`${fieldName}.toDate`}
            render={({ field: { onChange, value } }) => {
              return (
                <DatePicker
                  key='toDate'
                  disabled
                  onChange={onChange}
                  testid={`${fieldName}.toDate`}
                  selected={value}
                  hasError={getErrorMessage(`${fieldName}.toDate`)}
                  style={{ width: 120, color: '#8795A2' }}
                  size='sm'
                />
              );
            }}
          />
        </TimeWindowControl>
        <TimeWindowControl label='Selection Set'>
          <Controller
            control={control}
            name={`${fieldName}.assetSelectionSetId`}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  testid={`${fieldName}.selection-set-search`}
                  placeholder='Search component'
                  isLoading={loadingAssets}
                  options={assetOptions || []}
                  onChange={(val) => {
                    onChange(val.value);
                    handleToggleHeaders({
                      index: timeWindow.toggleId,
                      forceOpen: true,
                    });
                  }}
                  value={assetOptions?.find((option) => option.value === value)}
                  hasError={getErrorMessage(`${fieldName}.assetSelectionSetId`)}
                  style={{ width: 250 }}
                />
              );
            }}
          />
        </TimeWindowControl>
        <TimeWindowControl label='Property'>
          <Controller
            control={control}
            name={`${fieldName}.tagRefPropertyDefinitionId`}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  testid={`${fieldName}.tagRefPropertyDefinitionId.component-search`}
                  placeholder='Search for component'
                  options={tagRefOptions || []}
                  isLoading={loadingProperties}
                  onChange={(val) => {
                    setValue(`${fieldName}.tagIds`, null);
                    setPropertyValueOptions(val?.tags ?? []);
                    onChange(val.value);
                    handleToggleHeaders({
                      index: timeWindow.toggleId,
                      forceOpen: true,
                    });
                  }}
                  value={tagRefOptions?.find(
                    (option) => option.value === value
                  )}
                  hasError={getErrorMessage(
                    `${fieldName}.tagRefPropertyDefinitionId`
                  )}
                  style={{ width: 250 }}
                />
              );
            }}
          />
        </TimeWindowControl>
        <TimeWindowControl label='Property Value'>
          <Controller
            control={control}
            name={`${fieldName}.tagIds`}
            render={({ field: { onChange, value } }) => {
              return (
                <TreeSelect
                  testid={`${fieldName}.tag-ids`}
                  onChange={(val) => {
                    onChange(val);
                    refreshCompositeWeightedTimeWindowData();
                    handleToggleHeaders({
                      index: timeWindow.toggleId,
                      forceOpen: true,
                    });
                  }}
                  value={value}
                  options={propertyValueOptions}
                  isMulti={true}
                  hasError={getErrorMessage(`${fieldName}.tagIds`)}
                />
              );
            }}
          />
        </TimeWindowControl>
      </div>
      <div
        style={{
          flex: '0 1 auto',
          alignSelf: 'center',
          justifyContent: 'end',
          display: 'flex',
          flexDirection: 'row',
          width: '200px',
        }}
      >
        {isOpened && (
          <>
            <CircleButton
              key='clone'
              data-testid={`${fieldName}-clone`}
              icon={<Clone size='7px' />}
              title='Clone'
              onClick={() => {
                onClone({ ...getValues(fieldName) });
              }}
            />
            <CircleButton
              disabled={timeWindows.length === 1}
              key='delete'
              data-testid={`${fieldName}-delete`}
              icon={<Delete size='7px' />}
              title='Delete'
              onClick={() => {
                onRemove(index);
              }}
            />
            <CircleButton
              key='refresh'
              data-testid={`${fieldName}-refresh`}
              icon={<RefreshTW width={18} height={18} />}
              title='Refresh'
              onClick={() => {
                refreshCompositeWeightedTimeWindowData();
              }}
            />
          </>
        )}
        {!isOpened && hasErrors && (
          <Warning size='13px' className='m-1' fill='#AA0101' />
        )}
      </div>
    </div>
  );
};
